//#region imports
import React, { useMemo } from 'react';
import moment from 'moment';
import { Spacing, Colors } from "_styles";
import FlightNumber from './flight-number';
//#endregion

//#region helper functions
const getBackgroundColor = (type) => {
    if (type === 'departure') return Colors.PRM_LIGHT_BLUE;
    if (type === 'arrival') return Colors.LIGHT_GREEN;
    if (type === 'boarding') return "#e895a4";
    return 'white';
};
//#endregion

/**
 * FlightRowProps - A flight row component that takes all values as props
 * Used for displaying flight search results where flight data isn't in Redux
 * Visually consistent with FlightInfoCard
 */
const FlightRowProps = ({ flight, onClick }) => {

    // Extract flight data from props
    const flightNumber = flight?.flightNumber || '';
    const type = flight?.type || '';
    const origin = flight?.origin || flight?.departureAirport || '';
    const destination = flight?.destination || flight?.arrivalAirport || '';
    const operationTime = flight?.operationInfo?.time || flight?.operationTime || flight?.originalFlightTime || flight?.flightTime;
    const gate = flight?.gate && flight?.gate !== 'N/A' ? flight?.gate : '';
    const stand = flight?.stand && flight?.stand !== 'N/A' ? flight?.stand : '';
    const aircraftReg = flight?.aircraftRegistration || flight?.registration || '';
    const codeShares = flight?.CodeShareFlights || [];
    const operatingStatus = flight?.operatingStatus || '';
    const isCancelled = operatingStatus === 'Cancelled' || operatingStatus === 'cancelled' || flight?.cancelled;
    const isInPast = flight?.inPast;

    // Format display values
    const displayFlightNumber = useMemo(() => {
        return flightNumber?.includes('U2') ? flightNumber.replace('U2', 'EZY') : flightNumber;
    }, [flightNumber]);

    const formattedTime = useMemo(() => {
        if (!operationTime) return '';
        return moment(new Date(operationTime)).format("HH:mm").replace(":", "");
    }, [operationTime]);

    const displayAirport = type === 'arrival' ? origin : destination;

    const backgroundColor = useMemo(() => getBackgroundColor(type), [type]);

    const hasGate = gate && gate !== '';
    const hasStand = stand && stand !== '';

    // Container style
    const containerStyle = useMemo(() => ({
        backgroundColor,
        overflow: 'hidden',
        flex: 1,
        padding: Spacing.SCALE_2,
        margin: 0,
        opacity: isInPast ? 0.6 : 1.0,
        borderTopColor: isCancelled ? 'red' : 'transparent',
        borderTopWidth: isCancelled ? 5 : 0,
        borderTopStyle: isCancelled ? 'solid' : 'none',
        cursor: 'pointer',
        marginBottom: Spacing.SCALE_4,
        borderRadius: 2,
    }), [backgroundColor, isInPast, isCancelled]);

    // Status badge style
    const getStatusStyle = () => {
        let bgColor = 'white';
        let textColor = Colors.PRM_LIGHT_BLUE;
        let borderColor = Colors.PRM_LIGHT_BLUE;

        if (operatingStatus === 'Early') {
            bgColor = 'green';
            textColor = 'white';
            borderColor = 'green';
        } else if (operatingStatus === 'Delay') {
            bgColor = 'red';
            textColor = 'white';
            borderColor = 'red';
        } else if (operatingStatus === 'Cancelled') {
            bgColor = 'red';
            textColor = 'white';
            borderColor = 'red';
        }

        return {
            whiteSpace: "nowrap",
            fontSize: 8,
            textAlign: 'center',
            padding: Spacing.SCALE_2,
            margin: 0,
            border: '1px solid',
            borderRadius: 2.5,
            borderColor,
            color: textColor,
            backgroundColor: bgColor
        };
    };

    return (
        <div onClick={onClick} style={containerStyle}>
            {/* Top row: Flight number, airport, aircraft reg, status */}
            <div style={{ 
                display: 'flex', 
                justifyContent: "space-between", 
                flex: 1, 
                borderBottomWidth: 0.5, 
                borderBottomColor: 'white', 
                borderBottomStyle: 'solid' 
            }}>
                <div style={{ 
                    textAlign: 'left', 
                    color: 'white', 
                    fontWeight: 'bold', 
                    padding: 0, 
                    margin: 0, 
                    display: 'flex', 
                    alignItems: 'center' 
                }}>
                    <FlightNumber
                        flightNumber={displayFlightNumber}
                        codeShares={codeShares}
                    />
                    <span
                        data-tippy-content={type === "arrival" ? `Departure Airport: ${origin}` : `Arrival Airport: ${destination}`}
                        style={{ marginLeft: '1px', marginRight: '1px', fontSize: 14 }}
                    >
                        -{displayAirport}-
                    </span>
                    {aircraftReg && (
                        <span
                            data-tippy-content={`Aircraft Registration: ${aircraftReg}`}
                            style={{ marginLeft: '0px', fontSize: 14 }}
                        >
                            {aircraftReg.slice(-2)}
                        </span>
                    )}
                </div>
                <div style={{ 
                    alignItems: 'center', 
                    display: 'flex', 
                    justifyContent: 'flex-end', 
                    padding: 0, 
                    margin: 0 
                }} data-tippy-content="Flight Status">
                    {operatingStatus && (
                        <p style={getStatusStyle()}>
                            {operatingStatus.toUpperCase()}
                        </p>
                    )}
                </div>
            </div>

            {/* Bottom row: Time, Gate, Stand */}
            <div style={{ display: 'flex', justifyContent: "space-between", flex: 1 }}>
                <div style={{ display: 'flex' }}>
                    <div style={{ display: 'flex', justifyContent: 'left', alignItems: 'left' }} data-tippy-content="Flight Time">
                        <p style={{ textAlign: 'left', color: 'white', padding: 0, margin: 0 }}>
                            {formattedTime}
                        </p>
                    </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 7 }}>
                    {hasGate && (
                        <div>
                            <p style={{ color: 'white', padding: 0, margin: 0 }} data-tippy-content="GATE">
                                {`G${gate}`}
                            </p>
                        </div>
                    )}
                </div>
                <div>
                    {hasStand && (
                        <div>
                            <p style={{ color: 'white', padding: 0, margin: 0 }} data-tippy-content="STAND">
                                {`S${stand}`}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default React.memo(FlightRowProps, (prevProps, nextProps) => {
    return (
        prevProps.flight === nextProps.flight &&
        prevProps.flight?.gate === nextProps.flight?.gate &&
        prevProps.flight?.stand === nextProps.flight?.stand &&
        prevProps.flight?.operatingStatus === nextProps.flight?.operatingStatus
    );
});
